/* buildPages(): the sheet bodies for the current document + options (print.js buildPages). */
import type { Employee } from "../../../data/types";
import { annualEntitlement } from "../../../domain/payroll";
import { PAPERS } from "../papers";
import type { PrintData, SheetPage } from "./data";
import { ChequeBody, EnvelopeBody, idCardBodies, LabelBody, LeaveFormSheet, LetterSheet, SettlementSheet, TestBody, VoucherSheet } from "./DocSheets";
import { Letterhead, type PCtx } from "./kit";
import { PayslipBody, PayslipRoll } from "./PayslipSheets";
import { attendancePages, directoryPages, gosiPages, registerPages, slipRows, wpsPages } from "./TableSheets";
import { activeCode } from "../../../lib/country";

export interface BuildExtra { letterTo: string; letterRef: string }

/** Employees the document prints for (scope + department filter). */
export function targetEmps(c: PCtx, data: PrintData): Employee[] {
  const s = c.s;
  if (s.scope === "all" && (s.doc === "payslip" || s.doc === "label" || s.doc === "cheque")) return data.emps.filter((e) => !s.dept || e.dept === s.dept);
  return data.emp ? [data.emp] : [];
}

export function buildPages(c: PCtx, data: PrintData, adj: number, extra: BuildExtra): SheetPage[] {
  const s = c.s, d = c.d, fam = d.fam;
  const deptEmps = data.emps.filter((e) => !s.dept || e.dept === s.dept);
  const emp = data.emp;
  const empty: SheetPage[] = [];

  switch (s.doc) {
    case "test":
      return [{ key: "test", body: <TestBody c={c} d={d} />, bare: true }];

    case "payslip": {
      const list = targetEmps(c, data).filter((e) => data.slips.has(e.id));
      if (fam === "roll") return list.map((e) => ({ key: "ps" + e.id, body: <PayslipRoll c={c} e={e} p={data.slips.get(e.id)!} period={s.period} payDay={data.payDay} paperW={(PAPERS[s.paper] ?? PAPERS.R80).w} />, bare: true }));
      if (s.perSheet === 2 && s.orient === "portrait") {
        const out: SheetPage[] = [];
        const hk = s.head === "full" ? "simple" : s.head;
        for (let i = 0; i < list.length; i += 2) {
          const a = list[i], b = list[i + 1];
          out.push({
            key: "ps2-" + a.id, nohead: true,
            body: (
              <div className="twoup">
                <div className="half"><Letterhead c={c} kind={hk} /><PayslipBody c={c} e={a} p={data.slips.get(a.id)!} period={s.period} compact gosi={data.gosi} payDay={data.payDay} /></div>
                <div className="cut"><span>✂</span></div>
                <div className="half">{b && <><Letterhead c={c} kind={hk} /><PayslipBody c={c} e={b} p={data.slips.get(b.id)!} period={s.period} compact gosi={data.gosi} payDay={data.payDay} /></>}</div>
              </div>
            ),
          });
        }
        return out;
      }
      return list.map((e) => ({ key: "ps" + e.id, body: <><Letterhead c={c} kind={s.head} /><PayslipBody c={c} e={e} p={data.slips.get(e.id)!} period={s.period} compact={d.h < 240} gosi={data.gosi} payDay={data.payDay} /></> }));
    }

    case "letter": {
      if (!emp) return empty;
      const leave = data.leaves.find((l) => l.employeeId === emp.id && l.status !== "Rejected") ?? null;
      return [{ key: "letter", body: <LetterSheet c={c} kind={s.letter} e={emp} to={extra.letterTo} leave={leave} refNo={extra.letterRef || "HR/" + c.today.slice(0, 4) + "/" + (300 + emp.id)} /> }];
    }

    case "voucher": {
      if (!emp) return empty;
      const ln = data.loans.find((l) => l.employeeId === emp.id) ?? data.loans[0];
      if (!ln) return empty;
      const le = data.emps.find((e) => e.id === ln.employeeId) ?? emp;
      return [{ key: "voucher", body: <VoucherSheet c={c} l={ln} e={le} roll={fam === "roll"} />, bare: fam === "roll" }];
    }

    case "idcard":
      if (!emp) return empty;
      return idCardBodies(c, emp).map((b, i) => ({ key: "idc" + i, body: b, bare: true }));

    case "label": {
      const es = targetEmps(c, data);
      if (fam === "labelsheet") {
        const out: SheetPage[] = [];
        for (let i = 0; i < es.length; i += 24) out.push({ key: "ls" + i, bare: true, body: <div className="lsheet">{es.slice(i, i + 24).map((e) => <div className="lcell" key={e.id}><LabelBody c={c} e={e} small /></div>)}</div> });
        return out;
      }
      return es.map((e) => ({ key: "lbl" + e.id, body: <LabelBody c={c} e={e} small={d.h < 80} />, bare: true }));
    }

    case "envelope":
      if (!emp) return empty;
      return [{ key: "env", body: <EnvelopeBody c={c} e={emp} />, bare: true }];

    case "leaveform": {
      if (!emp) return empty;
      const lv = data.leaves.find((l) => l.employeeId === emp.id) ?? { type: "Annual", from: c.today, to: c.today, days: 1 };
      return [{ key: "lvf", body: <LeaveFormSheet c={c} e={emp} lv={lv} balance={data.balance} policies={data.policies} entitlement={annualEntitlement(emp, c.today, activeCode())} /> }];
    }

    case "settlement":
      if (!emp || (!data.settlement && !data.eosb)) return empty;
      return [{ key: "eos", body: <SettlementSheet c={c} e={emp} off={data.offboarding} st={data.settlement} eo={data.eosb} /> }];

    case "cheque":
      return targetEmps(c, data).filter((e) => data.slips.has(e.id)).map((e) => ({ key: "chq" + e.id, bare: true, body: <ChequeBody c={c} e={e} p={data.slips.get(e.id)!} period={s.period} /> }));

    case "register":
      return registerPages(c, adj, slipRows(deptEmps, data.slips), s.period, data.payDay);

    case "gosi":
      return gosiPages(c, adj, slipRows(data.emps, data.slips), s.period);

    case "wps":
      return wpsPages(c, adj, slipRows(data.emps, data.slips), data.run, s.period);

    case "directory":
      return directoryPages(c, adj, deptEmps);

    case "attendance":
      return attendancePages(c, adj, deptEmps, data.attendance, data.attPeriod, data.weekendDays);
  }
  return empty;
}
